import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from "react"
import {
  AccountCircleRounded,
  RadioButtonUncheckedRounded,
  ListRounded,
  GridViewRounded,
  CloseRounded
} from "@mui/icons-material"
import {
  Box,
  Button,
  Container,
  Grid,
  Icon,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Modal,
  Pagination,
  TextField,
  Typography
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import Navigation from "../components/navigation"
import UserCard from "../components/userCard"
import UserModal from "../components/userModal"
import { useUsers } from "../providers/users"
import { socket } from ".."
import { useEmbossers } from "../providers/embossers"
import debounce from "lodash.debounce"
import useWidth from "../hooks/window";
import { MultiplePropertiesArea } from "../components/propertyArea";
import { CustomSelect } from "../components/customSelect";
import { makeStyles } from "@mui/styles"
import {
  styles,
  stylesDark,
  Styles,
  useTheme
} from "../providers/theme"
import { FilterPillShape } from "../components/FilterPillShape";

const useStylesLight = makeStyles<any, Styles>({...styles})
const useStylesDark = makeStyles<any, Styles>({...stylesDark})

export default function UnassignedUsers() {
  const navigate = useNavigate()
  const {mode} = useTheme()
  const width = useWidth()
  const {
    users,
    search,
    setSearch,
    sortByName,
    setSortByName,
    sortByEmail,
    setSortByEmail,
    filterByStatus,
    setFilterByStatus,
    setFilterByRole,
    displayMode,
    setDisplayMode,
    pagesCount,
    currentPage,
    setCurrentPage,
    resultsListLength,
    itemsOnPage,
    setItemsOnPage,
    setUnassigned,
    selectedUser
  } = useUsers()
  const [localSearch, setLocalSearch] = useState<string>(search)
  const [modalContent, setModalContent] = useState<ReactNode>(<></>)
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const filterActive: boolean = filterByStatus !== ""
  const filtersList: {
    label: string,
    value: string,
    setFilterDispatch: Dispatch<SetStateAction<string>>
  }[] = [
    {
      label: "Status",
      value: filterByStatus,
      setFilterDispatch: setFilterByStatus
    }
  ]
  const title = 'Unassigned Users'
  const [distributors, setDistributors] = useState<string[]>([])
  const {availableManufacturers} = useEmbossers()
  const itemsOnPageValues: number[] = [15, 30, 45]
  let classesLight = useStylesLight(styles)
  let classesDark = useStylesDark(stylesDark)
  const classes = mode === 'light' ? classesLight : classesDark

  const searchUsers = (localSearch: string) => {
    setSearch(localSearch)
  }

  // eslint-disable-next-line
  const debouncedSearchUsers = useCallback(debounce(searchUsers, 300), [])

  useEffect(() => {
    debouncedSearchUsers(localSearch)

    return () => {
      debouncedSearchUsers.cancel()
    }
    // eslint-disable-next-line
  }, [localSearch])

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearch = event.target.value
    setLocalSearch(newSearch)
  }

  const onChangeItemsOnPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newItemsOnPage = parseInt(event.target.value)
    setItemsOnPage(newItemsOnPage)
  }

  useEffect(() => {
    setSortByEmail("default")
    setSortByName("default")
    setFilterByStatus("")
    setFilterByRole("")
    setUnassigned(true)
    socket.emit('get_distributors');
    socket.on('distributors_received', (data) => {
      if (data.error) {
        console.error(data.error)
      } else {
        setDistributors(data.distributors)
      }
    })
    return () => {
      socket.off('distributors_received')
    }
    // eslint-disable-next-line
  }, [])

  return (
    <Navigation
      title={title}
      actions={
        <>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="account-appbar"
            aria-haspopup="true"
            color="inherit"
            edge="end"
            onClick={() => navigate('/account')}
          >
            <AccountCircleRounded />
          </IconButton>
        </>
      }
    >
      <Container sx={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            width: "100%",
            display: "flex"
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "flex-end"
            }}
          >
            <Typography variant="h4" component="h1" my={2}>
              {title}
            </Typography>
            <Typography
              variant="subtitle1"
              component="h2"
              ml={2}
              my={2}
              sx={{ color: `${mode === "light" ? "#25252580" : "#FFFFFF80"}` }}
            >
              {`${resultsListLength} ${resultsListLength === 1 ? "result" : "results"}`}
            </Typography>
          </Box>
          <Box
            sx={{
              width: "auto",
              display: "flex",
              alignItems: "center"
            }}
          >
            <IconButton
              sx={{
                width: "50px",
                height: "50px",
                backgroundColor: displayMode === "list" ? (mode === "light" ? "#eee" : "#252525") : "transparent"
              }}
              onClick={() => {
                setDisplayMode('list')
              }}
            >
              <ListRounded />
            </IconButton>
            <IconButton
              sx={{
                width: "50px",
                height: "50px",
                backgroundColor: displayMode === "grid" ? (mode === "light" ? "#eee" : "#252525") : "transparent"
              }}
              onClick={() => {
                setDisplayMode('grid')
              }}
            >
              <GridViewRounded />
            </IconButton>
          </Box>
        </Box>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between"
          }}
        >
          <TextField
            sx={{
              width: "80%"
            }}
            defaultValue={search}
            label="Search"
            size="small"
            onChange={onChangeSearch}
          />
          <Button
            sx={{
              width: "10%",
              paddingInline: "2em !important",
              marginLeft: ".5em",
              border: `1px solid ${mode === "light" ? "#BBB" : "#666"}`,
              borderRadius: "10px",
              color: `${mode === "light" ? "#252525" : "#FFF"}`,
              "&:hover": {
                border: `1px solid ${mode === "light" ? "#252525" : "#FFF"}`,
                backgroundColor: `${mode === "light" ? "#252525" : "#FFF"}`,
                color: `${mode === "light" ? "#FFF" : "#252525"}`
              }
            }}
            onClick={() => {
              setModalContent(
                <Box
                  sx={{
                    backgroundColor: mode === 'light' ? "#F6F6F6" : "#252525",
                    borderRadius: "10px",
                    boxShadow: mode === 'light' ? "0px 3px 12px -1px rgba(0, 0, 0, 0.2)" : "0px 3px 12px -1px rgba(0, 0, 0, 0.5)",
                    padding: "1em",
                  }}
                >
                  <Typography
                    variant="h5"
                    component="h2"
                  >
                    Sort By
                  </Typography>
                  <MultiplePropertiesArea
                    topMargin={true}
                    paddingInline={true}
                    property={"Name"}
                    classes={classes}
                    children={
                      <CustomSelect
                        multiple={false}
                        items={[
                          {
                            value: "Default",
                            selected: "default" === sortByName.toLowerCase()
                          },
                          {
                            value: "Ascending",
                            selected: "ascending" === sortByName.toLowerCase()
                          },
                          {
                            value: "Descending",
                            selected: "descending" === sortByName.toLowerCase()
                          }
                        ]}
                        setFunction={setSortByName as Dispatch<SetStateAction<string>>}
                      />
                    }
                  />
                  <MultiplePropertiesArea
                    topMargin={true}
                    paddingInline={true}
                    property={"Email"}
                    classes={classes}
                    children={
                      <CustomSelect
                        multiple={false}
                        items={[
                          {
                            value: "Default",
                            selected: "default" === sortByEmail.toLowerCase()
                          },
                          {
                            value: "Ascending",
                            selected: "ascending" === sortByEmail.toLowerCase()
                          },
                          {
                            value: "Descending",
                            selected: "descending" === sortByEmail.toLowerCase()
                          }
                        ]}
                        setFunction={setSortByEmail as Dispatch<SetStateAction<string>>}
                      />
                    }
                  />
                </Box>
              )
              setModalOpen(true)
            }}
          >
            Sort
          </Button>
          <Button
            sx={{
              width: "10%",
              paddingInline: "2em !important",
              marginLeft: ".5em",
              border: `1px solid ${mode === "light" ? "#BBB" : "#666"}`,
              borderRadius: "10px",
              color: `${mode === "light" ? "#252525" : "#FFF"}`,
              "&:hover": {
                border: `1px solid ${mode === "light" ? "#252525" : "#FFF"}`,
                backgroundColor: `${mode === "light" ? "#252525" : "#FFF"}`,
                color: `${mode === "light" ? "#FFF" : "#252525"}`
              }
            }}
            onClick={() => {
              setModalContent(
                <Box
                  sx={{
                    backgroundColor: mode === 'light' ? "#F6F6F6" : "#252525",
                    borderRadius: "10px",
                    boxShadow: mode === 'light' ? "0px 3px 12px -1px rgba(0, 0, 0, 0.2)" : "0px 3px 12px -1px rgba(0, 0, 0, 0.5)",
                    padding: "1em"
                  }}
                >
                  <Typography
                    variant="h5"
                    component="h2"
                    ml={2}
                  >
                    Filter By
                  </Typography>
                  <MultiplePropertiesArea
                    topMargin={true}
                    property={"Status"}
                    classes={classes}
                    children={
                      <CustomSelect
                        multiple={true}
                        items={[
                          {
                            value: "Online",
                            selected: filterByStatus.toLowerCase().split(';').includes("online")
                          },
                          {
                            value: "Offline",
                            selected: filterByStatus.toLowerCase().split(';').includes("offline")
                          }
                        ]}
                        setFunction={setFilterByStatus as Dispatch<SetStateAction<string>>}
                      />
                    }
                  />
                </Box>
              )
              setModalOpen(true)
            }}
          >
            Filter
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            marginTop: "1em"
          }}
        >
          {
            filterActive &&
              <Typography
                variant="subtitle1"
                component="h2"
                sx={{
                  marginLeft: ".5em"
                }}
              >
                Filters:
              </Typography>
          }
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              width: "100%",
              height: "auto"
            }}
          >
            {
              filtersList.map((filter, index) =>
                filter.value !== "" &&
                  <React.Fragment key={index}>
                    {
                      filter.value.split(';').map((selectedItem, index) =>
                        <React.Fragment key={selectedItem}>
                          <FilterPillShape
                            itemValue={selectedItem}
                            filter={filter.value}
                            setFilterFunction={filter.setFilterDispatch}
                          />
                        </React.Fragment>
                      )
                    }
                  </React.Fragment>
              )
            }
            {
              filterActive &&
                <Box
                  sx={{
                    borderRadius: "5em",
                    width: "auto",
                    display: "flex",
                    gap: ".25em",
                    fontSize: ".75em",
                    alignItems: "center",
                    paddingInline: ".75em",
                    paddingBlock: ".25em",
                    marginLeft: ".5em",
                    marginBottom: ".5em",
                    cursor: "pointer",
                    color: mode === 'dark' ? "#F6F6F6" : "#252525",
                    transition: "background-color .25s, color .25s",
                    border: `1px solid ${mode === "light" ? "#BBB" : "#666"}`,
                    "&:hover": {
                      backgroundColor: mode === 'dark' ? "#F6F6F6" : "#252525",
                      color: mode === 'dark' ? "#252525" : "#F6F6F6"
                    }
                  }}
                  onClick={() => {
                    // eslint-disable-next-line
                    filtersList.map(filtersIterator => {
                      filtersIterator.setFilterDispatch("")
                    })
                  }}
                >
                  <p>
                    Clear All
                  </p>
                </Box>
            }
          </div>
        </div>
        {
          displayMode === 'grid' ?
            <Grid
              container
              spacing={2}
              sx={{
                justifyContent: "center",
                mt: 2,
                mb: 2
              }}
            >
              {
                users.length > 0 ?
                  users.map((user, index) =>
                    <Grid
                      item
                      key={index}
                    >
                      <UserCard
                        user={user}
                        displayMode={displayMode}
                        distributors={distributors.sort()}
                        manufacturersList={availableManufacturers.sort()}
                      />
                    </Grid>
                  )
                :
                  <Typography
                    gutterBottom
                    variant="h5"
                    component="h2"
                  >
                    No match
                  </Typography>
              }
            </Grid>
          :
            users.length > 0 ?
              <>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    paddingInline: "2em",
                    marginTop: "1.3em",
                    gap: ".5em"
                  }}
                >
                  <Icon sx={{ visibility: "hidden" }} >
                    <RadioButtonUncheckedRounded />
                  </Icon>
                  <p
                    style={{
                      width: "50px",
                      flexGrow: "1",
                      fontSize: "1.4em",
                      fontWeight: "bold",
                      margin: "0",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    Name
                  </p>
                  <p
                    style={{
                      width: "50px",
                      flexGrow: "1",
                      fontSize: "1.4em",
                      fontWeight: "bold",
                      margin: "0",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    Email
                  </p>
                  <Button
                    variant="contained"
                    sx={{
                      width: "50px",
                      maxWidth: "100px",
                      flexGrow: "1",
                      fontWeight: "bold",
                      margin: "0",
                      visibility: "hidden",
                      fontSize: `${width > 750 ? '.8em' : '.7em'}`
                    }}
                    size="small"
                  >
                    Open
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      width: "50px",
                      maxWidth: "100px",
                      flexGrow: "1",
                      fontWeight: "bold",
                      margin: "0",
                      visibility: "hidden",
                      fontSize: `${width > 750 ? '.8em' : '.7em'}`
                    }}
                    size="small"
                  >
                    Remove
                  </Button>
                </Box>
                <List
                  sx={{
                    width: "100%",
                    height: "auto"
                  }}
                >
                  {
                    users.length > 0 &&
                      users.map((user, index) =>
                        <ListItem
                          key={index}
                          disableGutters
                        >
                          <UserCard
                            user={user}
                            displayMode={displayMode}
                            distributors={distributors.sort()}
                            manufacturersList={availableManufacturers.sort()}
                          />
                        </ListItem>
                      )
                  }
                </List>
              </>
            :
              <Typography
                gutterBottom
                variant="h5"
                component="h2"
              >
                No match
              </Typography>
        }
        {
          pagesCount !== 0 &&
            <Box
              sx={{
                width: "100%",
                height: "auto",
                display: "flex",
                flexDirection: `${width < 1000 ? "column" : "row"}`,
                justifyContent: "space-between",
                alignItems: "center",
                marginBlock: "1em 2.5em"
              }}
            >
              <TextField
                sx={{
                  width: "150px",
                  position: "relative"
                }}
                fullWidth
                value={itemsOnPage}
                label="Items on page"
                size="small"
                select
                onChange={onChangeItemsOnPage}
              >
                {
                  itemsOnPageValues.map((itemsOnPageValue: number, index: number) =>
                    <MenuItem
                      key={index}
                      value={itemsOnPageValue}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "rgba(133, 210, 1, 0.3)",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "rgba(133, 210, 1, 0.3)",
                        },
                        "&:hover": {
                          backgroundColor: `${mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`
                        }
                      }}
                    >
                      {itemsOnPageValue}
                    </MenuItem>
                  )
                }
              </TextField>
              <Pagination
                sx={{
                  marginBlock: '1em',
                  position: "relative",
                  right: "50%",
                  display: "flex",
                  width: `${width < 1000 ? "100%" : "auto"}`,
                  justifyContent: "center",
                  transform: "translateX(50%)"
                }}
                count={pagesCount}
                page={currentPage}
                onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                  window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                  })
                  setCurrentPage(newPage)
                }}
                size="large"
              />
            </Box>
        }
        {
          modalOpen &&
            <Modal
              open={modalOpen}
              onClose={() => {
                setModalOpen(false)
              }}
            >
              <Box
                className={classes.modalContainer}
                sx={{
                  width: "50%",
                  minWidth: "300px",
                  maxWidth: "700px",
                  paddingTop: "1.5em !important",
                  paddingBottom: "1em"
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "calc(100% - 1em)",
                    overflowY: "auto",
                    marginTop: "1em",
                    borderRadius: "10px",
                    marginRight: "1.5em",
                    padding: "1em"
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "auto",
                      display: "flex",
                      flexDirection: "column"
                    }}
                  >
                    {modalContent}
                  </Box>
                </Box>
                <IconButton
                  className={classes.modalButtonClose}
                  onClick={() => {
                    setModalOpen(false)
                  }}
                >
                  <CloseRounded />
                </IconButton>
              </Box>
            </Modal>
        }
        {
          selectedUser &&
            <UserModal
              distributors={distributors.sort()}
              manufacturersList={availableManufacturers.sort()}
            />
        }
      </Container>
    </Navigation>
  )
}
